/**
 * @name HeaderBanner
 * @description To inform users about a lululemon
 * fact relevent to the site (free shipping, newsletter, etc.)
 * @class
 * @author Christine Hager <chhager@deloitte.com>
 * @example  <HeaderBanner banners={this.props.banners} />
 */
import * as React from "react";

import PropTypes from "prop-types";
import classNames from "classnames";
import { flow, isEqual } from "lodash";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { withABTestWrapper } from "@lululemon/mwa-ab-testing/esm";
import { withRouter } from "@lululemon/ecom-core";
import { PromoBanner } from "@lululemon/meganav";
import { log } from "@lululemon/mwa-utils";
import { trackEvent, EVENT_CATEGORY } from "@lululemon/mwa-analytics";
import Util from "../../lib/util";
import { encodedCookies } from "../../lib/cookies";
import { getResizeSubscriber } from "../../lib/breakpoints";
import * as headerBannerActions from "../../store/actions/mega-nav";
import {
  getGiftCard,
  getHeaderBannerHeight,
  getPages,
} from "../../store/selectors/mega-nav";
import EmailBanner from "../email-banner/email-banner";
import { getTopBannerInfo } from "../../util/getTopBannerInfo";
import "./header-banner.scss";
import { WelcomeOfferBannerModal } from "@lululemon/mwa-promotions";
import { publicConfig } from "../../util/public-config";

export class HeaderBanner extends React.Component {
  constructor(props) {
    super(props);

    this.cookies = encodedCookies();

    try {
      // 'lll_usr' cookie must be in object-like format in order to parse as JSON
      this.bannerCookie = JSON.parse(this.cookies.get("lll_usr"));
      // ensure the object contains an 'alerts' key
      this.bannerCookie.alerts = this.bannerCookie.alerts || {};
    } catch (e) {
      this.bannerCookie = { alerts: {} };
    }

    this.isPromoAppliedCookie = !!this.cookies.get("promoApplied");

    this.state = {
      showPromoBanner: false,
      togglePromoOrEmail: "promo",
      isBannerAnimating: false,
      mounted: false,
      allLocaleValues: {},
    };

    this.util = new Util("components/header-banner");

    this.handleResize = this.handleResize.bind(this);
    this.resizeSubscriber = getResizeSubscriber(this.handleResize);
    this.dismissBanner = this.dismissBanner.bind(this);
    this.dispatchHeaderHeight = this.dispatchHeaderHeight.bind(this);
    this.validateData = this.validateData.bind(this);
    this.renderOtherBanners = this.renderOtherBanners.bind(this);

    this.validateEmailBanner = this.validateData("email-banner").pass;
    this.validatePromoBanner = this.validateData("promo-banner").pass;

    this.bannerContentRef = React.createRef();
    this.bannerWrapperRef = React.createRef();
    this.openFocusRef = React.createRef();
  }

  componentDidMount() {
    // After mount, set real promo banner value
    this.setState(
      {
        mounted: true,
        showPromoBanner:
          this?.bannerCookie?.alerts?.["promoted-alert-bar"] ?? true,
      },
      () => {
        this.dispatchHeaderHeight();
      }
    );
  }

  componentDidUpdate(prevProps) {
    const { location, headerRef, pages } = this.props;

    const shouldBannerChange =
      this.validateEmailBanner && this.validatePromoBanner;

    if (prevProps.headerRef !== headerRef) {
      this.dispatchHeaderHeight();
    }

    if (
      shouldBannerChange &&
      prevProps.location.pathname !== location.pathname
    ) {
      this.setToggleBanner();
    }
    const newlocaleValues =
      pages?.[`${window.location.origin}/api${location.pathname}`] || {};
    const prevlocaleValues =
      prevProps?.pages?.[`${window.location.origin}/api${location.pathname}`] ||
      {};

    if (!isEqual(prevlocaleValues, newlocaleValues)) {
      const getAllLocalePid =
        newlocaleValues?.data?.productDetailPageData?.allLocalePids;
      const getAllNValue = newlocaleValues?.allLocaleNvalues;

      this.setState({
        allLocaleValues: getAllLocalePid || getAllNValue,
      });
    }
  }

  componentWillUnmount() {
    this.resizeSubscriber.unsubscribe();
    if (this.bannerChangeTimeout) {
      clearTimeout(this.bannerChangeTimeout);
    }

    if (this.bannerFadeInTimeout) {
      clearTimeout(this.bannerFadeInTimeout);
    }

    if (this.bannerLeaveTimeout) {
      clearTimeout(this.bannerLeaveTimeout);
    }
  }

  setBannerCookie(cookie) {
    this.cookies.set("lll_usr", JSON.stringify(cookie), {
      domain: ".lululemon.com",
    });
  }

  setToggleBanner() {
    const { location } = this.props;
    const { togglePromoOrEmail } = this.state;
    const isEmailSignedUp = this.cookies.get("lll-email-signed-up") === "true";

    // Always show promo banner on PDP, if a promo code is applied, or after signing up for email,
    const alwaysShowPromo =
      location.pathname.startsWith("/p/") ||
      this.isPromoAppliedCookie ||
      isEmailSignedUp;
    const updateTogglePromoOrEmail =
      togglePromoOrEmail === "email" || alwaysShowPromo ? "promo" : "email";

    if (togglePromoOrEmail !== updateTogglePromoOrEmail) {
      this.toggleBannerContents(updateTogglePromoOrEmail);
    }
  }

  toggleBannerContents(bannerCookie) {
    // START FADING OUT CURRENT BANNER
    this.setState({ isBannerAnimating: true });

    // SWITCH TO NEW BANNER CONTENT AFTER 1 SECOND (BANNER IS STILL OPACITY: 0)

    this.setState(
      {
        togglePromoOrEmail: bannerCookie,
      },
      () => {
        // HEIGHT OF .header-banner ADJUSTS TO MATCH NEW CONTENTS
        this.dispatchHeaderHeight();

        // NEW BANNER FADES IN AFTER 1 SECOND
        this.setState({ isBannerAnimating: false });
      }
    );
  }

  closeBanner() {
    // START FADING OUT PROMO BANNER
    this.setState({ isBannerAnimating: true });

    this.bannerLeaveTimeout = setTimeout(() => {
      // REMOVE BANNER AFTER 1 SECOND
      this.setState({ showPromoBanner: false }, () => {
        // update bannerHeight once promo banner is gone
        this.dispatchHeaderHeight();

        this.setState({ isBannerAnimating: false }, () => {
          // update headerHeight after banner gone (used for other PDP elements)
          this.dispatchHeaderHeight();
        });
      });
    }, 1000);
  }

  validateData(type) {
    const { banners } = this.props;
    const bannerData = banners.filter(
      (banner) => banner.fields.bannerType === type
    )[0]?.fields;
    let required;

    if (bannerData) {
      const rules = [];
      if (type === "email-banner") {
        rules.push(
          {
            name: `${type}.banner-headline`,
            value: bannerData.headline,
          },
          {
            name: `${type}.banner-link-text`,
            value: bannerData.label,
          },
          {
            name: `${type}.modal-landscape-imageurl`,
            value: bannerData.modalLandscapeImageUrl,
          },
          {
            name: `${type}.modal-portrait-imageurl`,
            value: bannerData.modalPortraitImageUrl,
          },
          {
            name: `${type}.modal-title`,
            value: bannerData.modalTitle,
          },
          {
            name: `${type}.modal-body`,
            value: bannerData.modalBody,
          },
          {
            name: `${type}.modal-submit-buttonlabel`,
            value: bannerData.modalSubmitButtonLabel,
          },
          {
            name: `${type}.modal-footer`,
            value: bannerData.modalFooter,
          },
          {
            name: `${type}.success-messages`,
            value: bannerData.success,
          },
          {
            name: `${type}.errors`,
            value: bannerData.errors,
          }
        );
      } else {
        rules.push(
          {
            name: `${type}.headline`,
            value: bannerData.headline,
          },
          {
            name: `${type}.text-box-default-text`,
            value: bannerData.label,
          }
        );
      }
      required = this.util.requiredData(rules);
      if (!required.pass) {
        log.warning(
          "Missed or misconfigured required data for",
          type,
          required.missing
        );
      }
    } else {
      required = {
        pass: false,
        missing: [],
      };
    }

    return required;
  }

  dismissBanner(dismissProps) {
    const { headline, label } = dismissProps;

    trackEvent(EVENT_CATEGORY.COMPONENT_EVENT, {
      component: {
        id: "top-banner-close",
        type: "banner",
        text: `${headline} ${label}`,
        position: -1,
        totalCount: -1,
      },
    });

    this.bannerCookie.alerts["promoted-alert-bar"] = false;
    this.setBannerCookie(this.bannerCookie);
    this.closeBanner();
  }

  handleResize() {
    this.dispatchHeaderHeight();
  }

  dispatchHeaderHeight() {
    const { setBannerAndHeaderHeight, headerRef } = this.props;
    const bannerRef = this.bannerContentRef.current;
    const bannerWrapperRef = this.bannerWrapperRef.current;

    if (bannerRef) {
      setBannerAndHeaderHeight(bannerRef, headerRef);
    } else if (bannerWrapperRef) {
      setBannerAndHeaderHeight(bannerWrapperRef, headerRef);
    }
  }

  validateTarget(page, target, banners) {
    const specificBanner = banners?.find(
      (banner) =>
        banner.fields?.gender === target && banner.fields?.pageType === page
    );
    const generalBanner = banners?.find(
      (banner) =>
        banner.fields?.gender === "everyone" && banner.fields?.pageType === page
    );

    return specificBanner ? specificBanner : generalBanner;
  }

  getPromoBannerData(page, target, data) {
    const banners = data?.fields?.genderBanners;

    if (
      (page === "category-detail" || page === "product-detail") &&
      target === "everyone"
    ) {
      const hasWomenBanner = banners?.find(
        (banner) =>
          banner?.fields?.pageType === page &&
          banner?.fields?.gender === "women"
      );
      if (!hasWomenBanner) {
        return banners?.find(
          (banner) =>
            banner?.fields?.pageType === page &&
            banner?.fields?.gender === "everyone"
        );
      }

      return hasWomenBanner;
    }

    if (page === "story" && !["women", "men"].includes(target)) {
      return banners?.find(
        (banner) =>
          banner?.fields?.pageType === page &&
          banner?.fields?.gender === "everyone"
      );
    }

    return this.validateTarget(page, target, banners);
  }

  renderOtherBanners() {
    const { showPromoBanner, togglePromoOrEmail, mounted } = this.state;
    const { banners, router } = this.props;

    const { target, page } = getTopBannerInfo(router.asPath);

    let bannerContent;
    const shouldShowPromoBanner =
      showPromoBanner &&
      this.validatePromoBanner &&
      (togglePromoOrEmail === "promo" || !this.validateEmailBanner);
    // only serve banner on client-side, to avoid a flash on load if the banner has been dismissed
    if (shouldShowPromoBanner && mounted) {
      const promoBannerData = banners.filter(
        (banner) => banner.fields.bannerType === "promo-banner"
      )[0];

      const bannerData = this.getPromoBannerData(page, target, promoBannerData);
      const { fields } = bannerData || promoBannerData;
      const { headline, webRoute, label } = fields;

      bannerContent = (
        <PromoBanner
          headline={headline}
          link={webRoute}
          text={label}
          onDismiss={() => this.dismissBanner({ headline, label })}
          onAnimationEnd={this.dispatchHeaderHeight}
          ref={this.bannerContentRef}
        />
      );
    }

    if (
      showPromoBanner &&
      this.validateEmailBanner &&
      (togglePromoOrEmail === "email" || !this.validatePromoBanner)
    ) {
      const emailBannerData = banners.filter(
        (banner) => banner.fields.bannerType === "email-banner"
      )[0];
      const { headline, label } = emailBannerData.fields;

      bannerContent = (
        <EmailBanner
          bannerRef={this.bannerContentRef}
          onDismissBanner={() => this.dismissBanner({ headline, label })}
          bannerProps={emailBannerData.fields}
        />
      );
    }

    return bannerContent;
  }

  // check out which banner we should display
  getBannerContent() {
    const { banners } = this.props;

    if (!banners || !banners.length) {
      log.warning('Missing "banners" prop on HeaderBanner.');
      return null;
    }

    let bannerContent;

    // always check welcome offer first
    // if the welcome offer doesn't exsit, we go with the previous logic as children
    const welcomeOfferContent = banners.filter(
      (banner) => banner.fields.bannerType === "welcome-offer-banner"
    )[0];

    const apiHost = `${publicConfig?.apiHost}`;

    if (
      welcomeOfferContent &&
      welcomeOfferContent.fields &&
      this.props.isWelcomeOfferEnabled
    ) {
      bannerContent = (
        <div className="welcome-offer-container">
          <WelcomeOfferBannerModal
            locale={{ lang: this.props.locale }}
            contentData={welcomeOfferContent.fields}
            baseUrl={apiHost}
          >
            {this.renderOtherBanners()}
          </WelcomeOfferBannerModal>
        </div>
      );
    } else {
      // cover the case there is no welcome offer banner content
      bannerContent = this.renderOtherBanners();
    }

    return bannerContent;
  }

  render() {
    const { isBannerAnimating, mounted } = this.state;
    const { bannerHeight } = this.props;

    let bannerContent;
    // create placeholder for CLS TODO Find way to look for banner enabled serverside
    if (!mounted) {
      bannerContent = (
        <div
          style={{
            height: "34px",
            width: "100%",
          }}
        />
      );
    } else {
      bannerContent = this.getBannerContent();
    }

    return (
      <div
        className={classNames("header-banner", {
          "header-banner--hidden": isBannerAnimating,
        })}
        style={bannerHeight ? { height: `${bannerHeight}px` } : null}
      >
        <div ref={this.bannerWrapperRef}>{bannerContent}</div>
      </div>
    );
  }
}

HeaderBanner.defaultProps = {
  headerRef: null,
  isWelcomeOfferEnabled: true,
};

HeaderBanner.propTypes = {
  banners: PropTypes.arrayOf(
    PropTypes.shape({
      fields: PropTypes.shape({
        name: PropTypes.string,
        bannerType: PropTypes.string,
        headline: PropTypes.string,
        heading: PropTypes.string,
        label: PropTypes.string,
        modalLandscapeImageUrl: PropTypes.string,
        modalPortraitImageUrl: PropTypes.string,
        modalTitle: PropTypes.string,
        modalBody: PropTypes.string,
        modalSubmitButtonLabel: PropTypes.string,
        modalFooter: PropTypes.string,
      }),
    })
  ).isRequired,
  setBannerAndHeaderHeight: PropTypes.func.isRequired,
  location: PropTypes.shape({
    asPath: PropTypes.string,
    location: PropTypes.shape({}),
    push: PropTypes.func,
    replace: PropTypes.func,
    prefetch: PropTypes.func,
    pushRoute: PropTypes.func,
    replaceRoute: PropTypes.func,
    prefetchRoute: PropTypes.func,
  }).isRequired,
  router: PropTypes.shape({}).isRequired,
  bannerHeight: PropTypes.number.isRequired,
  headerRef: PropTypes.shape({}),
  isWelcomeOfferEnabled: PropTypes.bool.isRequired,
  locale: PropTypes.string,
};

const mapStateToProps = ({ rootReducer }) => ({
  bannerHeight: getHeaderBannerHeight(rootReducer),
  pages: getPages(rootReducer),
  giftCardRedux: getGiftCard(rootReducer),
});

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      ...headerBannerActions,
      dispatch,
    },
    dispatch
  );
}

export default flow(
  connect(mapStateToProps, mapDispatchToProps),
  withABTestWrapper({
    name: "header-banner",
    disableFoocMask: true,
    abTestEndDate: "06/01/2023",
  })
)(withRouter(HeaderBanner));
